import { atom, useAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRecordHotkeys } from 'react-hotkeys-hook';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  PopoverDrawer,
  PopoverDrawerContent,
  PopoverDrawerTrigger,
} from '@/components/ui/monbile-responsive-popover-drawer';
import ClinicianOtherInput from '@/components/ui/select/clinician-type-select/clinician-other-input';
import { SpecialtiesLoadError } from '@/components/ui/select/clinician-type-select/specialties-load-error';
import {
  OTHER_SPECIALTY_ID,
  useSelectedSpecialtyDisplayName,
} from '@/components/ui/select/clinician-type-select/useSelectedSpecialtyDisplayName';
import { useSpecialtySearch } from '@/components/ui/select/clinician-type-select/useSpecialtySearch';
import { useQuerySpecialties } from '@/hooks/query/useQuerySpecialties';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { cn } from '@/lib/cn';
import { type ClinicianTypeInput } from '@/lib/types';
import { ComboboxTrigger } from '@heidi/design-system/combobox';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@heidi/design-system/command';
import { DrawerHeader, DrawerTitle } from '@heidi/design-system/drawer';
import { ScrollArea } from '@heidi/design-system/scroll-area';
import { Skeleton } from '@heidi/design-system/skeleton';
import { TypographyP2 } from '@heidi/design-system/typography';

import { useOnboardingClinicianTypeSelectHotkeys } from '@heidi/growth';
import { personalizationSharedMessages } from '../personalization-shared.messages';

const isClinicianSelectShortcutsDisabledAtom = atom<boolean>(false);

type Props = {
  selectedSpecialty?: { id: string; name: string; group: { id: string; name: string } };
  setSelectedSpecialty: (specialty: ClinicianTypeInput) => void;
  otherSpecialty?: string;
  setOtherSpecialty: (value?: string) => void;
  className?: string;
  disabled?: boolean;
  drawerTitle?: string;
  countryCode?: string;
};

export const OnboardingClinicianTypeSelect = ({
  selectedSpecialty,
  setSelectedSpecialty,
  otherSpecialty,
  setOtherSpecialty,
  className,
  disabled,
  drawerTitle,
  countryCode,
}: Props) => {
  const intl = useIntl();
  const otherLabel = intl.formatMessage(personalizationSharedMessages.other);
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [isKeyboardMode, setIsKeyboardMode] = useState(true);
  const [isShortcutsDisabled] = useAtom(isClinicianSelectShortcutsDisabledAtom);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [, { stop }] = useRecordHotkeys();

  // `isPending` (no cached data yet) gates the trigger — NOT `isFetching`,
  // which also fires on background revalidation of an already-valid list and
  // would flicker-disable the trigger. A country change re-keys the query, so
  // isPending covers first load and every country switch; after an error it is
  // false, keeping the inline error state reachable.
  const {
    isPending,
    isError,
    error,
    data: specialties,
  } = useQuerySpecialties((data) => data.specialties, {
    country: countryCode,
  });
  const { search: searchSpecialties, isReady } = useSpecialtySearch(specialties);

  const filteredSpecialties = useMemo(() => {
    if (!search || !isReady) return specialties || [];
    return searchSpecialties(search);
  }, [search, searchSpecialties, specialties, isReady]);

  const displaySpecialties = useMemo(() => {
    if (filteredSpecialties.length === 0 && search.trim()) {
      return [
        {
          id: OTHER_SPECIALTY_ID,
          name: search.trim(),
          localizedName: search.trim(),
          group: {
            id: 'other',
            name: otherLabel,
          },
        },
      ];
    }
    return filteredSpecialties;
  }, [filteredSpecialties, search, otherLabel]);

  const selectedSpecialtyDisplayName = useSelectedSpecialtyDisplayName(
    selectedSpecialty,
    specialties
  );

  useEffect(() => {
    setHighlightedIndex(0);
  }, [search, open]);

  useEffect(() => {
    if (open && itemRefs.current?.at(highlightedIndex) && isKeyboardMode) {
      itemRefs.current.at(highlightedIndex)?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [highlightedIndex, open, isKeyboardMode]);

  const onSpecialtySelected = useCallback(
    (specialtyId: string) => {
      const specialty = displaySpecialties.find((spec) => spec.id === specialtyId) || {
        id: OTHER_SPECIALTY_ID,
        name: otherLabel,
        group: { id: 'other', name: otherLabel },
      };

      if (specialty.id === OTHER_SPECIALTY_ID && specialty.group.id === 'other') {
        setSelectedSpecialty({
          id: OTHER_SPECIALTY_ID,
          name: otherLabel,
          group: { id: 'other', name: otherLabel },
        });
        setOtherSpecialty(specialty.name);
      } else {
        setSelectedSpecialty(specialty);
        setOtherSpecialty(undefined);
      }

      setOpen(false);
      setSearch('');
      stop();
    },
    [displaySpecialties, otherLabel, setOtherSpecialty, setSelectedSpecialty, stop]
  );

  useOnboardingClinicianTypeSelectHotkeys({
    open,
    isShortcutsDisabled,
    displaySpecialties,
    highlightedIndex,
    setIsKeyboardMode,
    setHighlightedIndex,
    setOpen,
    onSpecialtySelected,
    stop,
  });

  const handleMouseMove = () => {
    setIsKeyboardMode(false);
  };

  return (
    <div className={cn('flex w-full flex-col gap-3', className)}>
      <div className="w-full">
        <PopoverDrawer open={open} onOpenChange={setOpen}>
          <PopoverDrawerTrigger asChild>
            <ComboboxTrigger
              open={open}
              disabled={disabled || isPending}
              isPlaceholder={!selectedSpecialty}
              role="combobox"
              onKeyDown={(e) => {
                if (e.key === 'Tab' && open) {
                  setOpen(false);
                  return;
                }
                if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
                  e.preventDefault();
                  setOpen(true);
                }
              }}
            >
              {selectedSpecialty ? (
                <TypographyP2 className={'truncate text-left'}>
                  {selectedSpecialtyDisplayName}
                </TypographyP2>
              ) : (
                <FormattedMessage {...personalizationSharedMessages.selectClinician} />
              )}
            </ComboboxTrigger>
          </PopoverDrawerTrigger>
          <PopoverDrawerContent
            className={
              isMobile
                ? 'px-0 pb-4'
                : 'max-h-[--radix-popover-content-available-height] w-[--radix-popover-trigger-width] max-w-[var(--radix-popover-trigger-width)] p-0'
            }
            sideOffset={4}
            align="start"
            side="bottom"
            onKeyDownCapture={(e) => {
              if (e.key === 'Tab') {
                setOpen(false);
              }
            }}
          >
            {isMobile && (
              <DrawerHeader>
                <DrawerTitle>
                  {drawerTitle ??
                    intl.formatMessage(personalizationSharedMessages.selectClinician)}
                </DrawerTitle>
              </DrawerHeader>
            )}
            <Command shouldFilter={false}>
              <CommandInput
                placeholder={intl.formatMessage(
                  personalizationSharedMessages.searchClinicianTypes
                )}
                onValueChange={setSearch}
              />

              {isPending ? (
                <div className="p-1">
                  <Skeleton className="h-8 w-full" />
                </div>
              ) : null}
              {isError ? (
                <SpecialtiesLoadError error={error} className="p-3" />
              ) : (
                <CommandEmpty>
                  <FormattedMessage {...personalizationSharedMessages.noSpecialtyFound} />
                </CommandEmpty>
              )}
              <ScrollArea
                className={
                  isMobile
                    ? 'max-h-[60vh] overflow-auto'
                    : 'max-h-[220px] overflow-scroll sm:overflow-auto'
                }
                viewportClassName="[&>div]:!min-w-0 [&>div]:!block"
              >
                <CommandGroup onMouseMove={handleMouseMove}>
                  {displaySpecialties.map((specialty, index) => (
                    <CommandItem
                      ref={(el: HTMLDivElement | null) => {
                        itemRefs.current[index] = el;
                      }}
                      className={cn(
                        'flex w-full min-w-0 items-center justify-between gap-3',
                        isKeyboardMode
                          ? [
                              'data-[selected=true]:bg-transparent',
                              index === highlightedIndex && 'bg-background-secondary',
                            ]
                          : ['hover:bg-background-secondary']
                      )}
                      key={specialty.id}
                      value={specialty.id}
                      onSelect={() => onSpecialtySelected(specialty.id)}
                    >
                      <TypographyP2 className={'truncate text-left'}>
                        {specialty.localizedName}
                      </TypographyP2>
                    </CommandItem>
                  ))}
                </CommandGroup>
              </ScrollArea>
            </Command>
          </PopoverDrawerContent>
        </PopoverDrawer>
      </div>
      {(selectedSpecialty?.id.startsWith('other') ||
        selectedSpecialty?.name === otherLabel) && (
        <div className="w-full">
          <ClinicianOtherInput
            className={'h-full min-h-[34px]'}
            wrapperClassName={'h-full min-h-[34px]'}
            otherSpecialty={otherSpecialty}
            setOtherSpecialty={setOtherSpecialty}
          />
        </div>
      )}
    </div>
  );
};
